import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { TransferReviewStatus } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { transactionKeys } from "./useTransactions";
import { dashboardKeys } from "@/pages/dashboard/hooks/useDashboard";

// Query keys — exported factory (check:query-keys bans literal arrays).
export const transferReviewKeys = {
  all: ["transfer-review-queue"] as const,
  lists: () => [...transferReviewKeys.all, "list"] as const,
  list: (status?: TransferReviewStatus) => [...transferReviewKeys.lists(), { status }] as const,
  details: () => [...transferReviewKeys.all, "detail"] as const,
};

export function useTransferReviewQueue(status?: TransferReviewStatus) {
  return useQuery({
    queryKey: transferReviewKeys.list(status),
    queryFn: () => apiClient.getTransferReviewQueue(status),
  });
}

export function useAcceptTransferReviewEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiClient.acceptTransferReviewEntry(id),
    onSuccess: () => {
      // An accepted pair changes report math: transactions + dashboard must
      // refetch alongside the queue itself.
      queryClient.invalidateQueries({ queryKey: transferReviewKeys.all });
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      toast({
        title: "Transfer linked",
        description: "Both legs are flagged as a transfer and excluded from spending reports.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to accept the transfer candidate",
        variant: "destructive",
      });
    },
  });
}

export function useDismissTransferReviewEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiClient.dismissTransferReviewEntry(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transferReviewKeys.all });
      toast({ title: "Dismissed", description: "The candidate was dismissed with your reason." });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to dismiss the transfer candidate",
        variant: "destructive",
      });
    },
  });
}
